'use client';

import dynamic from 'next/dynamic';
import { Fragment, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import logCellCoverageCalloutTapped from '@alltrails/amplitude/events/logCellCoverageCalloutTapped';
import logCellCoverageModalViewed from '@alltrails/amplitude/events/logCellCoverageModalViewed';
import logTrailDetailsActionTapped from '@alltrails/amplitude/events/logTrailDetailsActionTapped';
import logTrailDetailsHighlightsViewed from '@alltrails/amplitude/events/logTrailDetailsHighlightsViewed';
import logTrailDetailsHighlightTapped from '@alltrails/amplitude/events/logTrailDetailsHighlightTapped';
import logTrailDetailsWebtoAppModuleClicked from '@alltrails/amplitude/events/logTrailDetailsWebtoAppModuleClicked';
import useUser from '@alltrails/context/hooks/useUser';
import { isSubscriber } from '@alltrails/context/utils/isSubscriber';
import useIsVisible from '@alltrails/core/hooks/useIsVisible';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import LinkOrNativeAnchor from '@alltrails/denali/components/LinkOrNativeAnchor';
import TableRow from '@alltrails/denali/components/TableRow';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import ChevronSm from '@alltrails/denali/icons/ChevronSm';
import type { TrailPage as TrailPageType } from '@alltrails/trails/types/trailPage';
import type TrailDetails from '@alltrails/types/TrailDetails';
import type TrailHighlight from '@alltrails/types/TrailHighlight';
import getTrailPageType from '@/utils/getTrailPageType';
import getTrailHighlightIcon from './getTrailHighlightIcon';
import styles from './TrailHighlights.module.scss';

const SpottyServiceModal = dynamic(() => import('./SpottyServiceModal'), { ssr: false });
const CellConnectivityModal = dynamic(() => import('../CellConnectivity/CellConnectivityModal'), { ssr: false });
const HitTheTrailModal = dynamic(() => import('@/components/HitTheTrailModal/HitTheTrailModal'), { ssr: false });

type TrailHighlightsProps = {
  highlights: TrailHighlight[];
  trailDetails: TrailDetails;
  trailPage: TrailPageType;
};

const TrailHighlights = ({ highlights, trailDetails, trailPage }: TrailHighlightsProps) => {
  const isMobile = useIsMobileSizedScreen();
  const user = useUser();
  const languageRegionCode = useLanguageRegionCode();
  const [isSpottyServiceOpen, setIsSpottyServiceOpen] = useState(false);
  const [isDownloadInAppOpen, setIsDownloadInAppOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const hasLoggedViewEvent = useRef(false);
  const isVisible = useIsVisible(containerRef);

  useEffect(() => {
    if (isVisible && !hasLoggedViewEvent.current && highlights.length > 0) {
      logTrailDetailsHighlightsViewed({
        highlight_count: highlights.length,
        highlight_types: highlights.map(h => h.type),
        trail_id: trailDetails.id
      });
      hasLoggedViewEvent.current = true;
    }
  }, [isVisible, highlights, trailDetails.id]);

  const logHighlightTapped = (highlight: TrailHighlight, index: number) => {
    logTrailDetailsHighlightTapped({
      highlight_type: highlight.type,
      index,
      slug: highlight.slug,
      subtitle: highlight.subtitle,
      title: highlight.title,
      trail_id: trailDetails.id
    });
  };

  const openSpottyService = (highlight: TrailHighlight, index: number) => {
    logHighlightTapped(highlight, index);
    logCellCoverageCalloutTapped({ trail_id: trailDetails.id });
    logCellCoverageModalViewed({ trail_id: trailDetails.id });
    setIsSpottyServiceOpen(true);
  };

  const logDownloadInAppFromHighlights = () => {
    const trailPageType = getTrailPageType(trailDetails);
    logTrailDetailsActionTapped({
      trail_id: String(trailDetails.id),
      trail_page_action: 'download in app',
      trail_page_location: 'highlights',
      trail_page_type: trailPageType
    });
    logTrailDetailsWebtoAppModuleClicked({
      trail_id: String(trailDetails.id),
      trail_page_type: trailPageType,
      was_plus_when_web_to_app_started: isSubscriber(user),
      web_to_app_action: 'download in app'
    });
  };

  const openDownloadInAppFromSpottyService = () => {
    logDownloadInAppFromHighlights();
    setIsSpottyServiceOpen(false);
    setIsDownloadInAppOpen(true);
  };

  return (
    <div className={styles.container} ref={containerRef}>
      {highlights.map((highlight, index) => {
        const Icon = getTrailHighlightIcon(highlight.type);
        const isLowCellService = highlight.type === 'info_low-cell-service';
        const row = (
          <TableRow
            className={styles.row}
            label={<span className={styles.title}>{highlight.title}</span>}
            description={highlight.subtitle}
            leftContent={<Icon />}
            rightContent={highlight.slug || isLowCellService ? <ChevronSm orientation="right" color="--color-text-primary" /> : undefined}
          />
        );

        if (isLowCellService) {
          return (
            <button key={highlight.type} type="button" className={styles.button} onClick={() => openSpottyService(highlight, index)}>
              {row}
            </button>
          );
        }

        if (highlight.slug) {
          return (
            <LinkOrNativeAnchor
              key={highlight.type}
              href={wrapUrlSafe(`/${highlight.slug}`, languageRegionCode)}
              className={styles.link}
              target="_blank"
              onClick={() => logHighlightTapped(highlight, index)}
            >
              {row}
            </LinkOrNativeAnchor>
          );
        }

        return <Fragment key={highlight.type}>{row}</Fragment>;
      })}
      {isSpottyServiceOpen &&
        (isMobile ? (
          <CellConnectivityModal
            isOpen={isSpottyServiceOpen}
            onCloseRequest={() => setIsSpottyServiceOpen(false)}
            onDownloadInApp={logDownloadInAppFromHighlights}
            trailDetails={trailDetails}
          />
        ) : (
          <SpottyServiceModal
            isOpen={isSpottyServiceOpen}
            onCloseRequest={() => setIsSpottyServiceOpen(false)}
            onDownloadInApp={openDownloadInAppFromSpottyService}
          />
        ))}
      {isDownloadInAppOpen && (
        <HitTheTrailModal
          includeDownloadInApp={false}
          includeExportMapFile={false}
          includeOpenInApp={false}
          includeSendToGarmin={false}
          isOpen={isDownloadInAppOpen}
          onCloseRequest={() => setIsDownloadInAppOpen(false)}
          steps={['download']}
          title={<FormattedMessage defaultMessage="Download in app" />}
          trailDetails={trailDetails}
          trailPage={trailPage}
        />
      )}
    </div>
  );
};

export default TrailHighlights;
